import React from 'react';
import { Button } from 'react-bootstrap';
import { FileText, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useQuoteSettings } from '../../../../context/QuoteSettingsContext';

// Quote settings section — numbering and validity defaults. Self-contained on
// QuoteSettingsContext. Rendered only when the tenant has `invoicing_quotes`
// (gated in the CompanySettings section registry). Uses the global settings
// styles from the CompanySettings shell.
const QuotesSection = () => {
  const { quoteSettings, updateQuoteSetting, saveQuoteSettings } = useQuoteSettings();

  const saveQuoteNumbering = async () => {
    try {
      await saveQuoteSettings('quoteNumbering');
      toast.success("Quote numbering settings saved successfully");
    } catch (error) {
      toast.error("Failed to save quote numbering settings");
    }
  };

  return (
    <>
      {/* Quote Numbering Section */}
      <div className="modern-card" id="offertnumrering">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <FileText size={24} />
              Offertnumrering
            </h2>
            <p className="section-subtitle">
              Konfigurera hur dina offertnummer genereras
            </p>
          </div>
          <div className="action-buttons">
            <Button
              variant="primary"
              className="ms-2"
              onClick={saveQuoteNumbering}
            >
              <Save className="me-2" size={16} />
              Spara offertnumrering
            </Button>
          </div>
        </div>

        <div className="modern-card-body">
          <div className="company-form-grid">
            <div className="form-section-wrapper">
              <div className="section-header-external">
                <div className="section-icon">
                  <FileText size={20} />
                </div>
                <div>
                  <h3 className="section-heading-external">Numreringsinställningar</h3>
                  <p className="section-description">Konfigurera prefix, startnummer och format för offerter</p>
                </div>
              </div>

              <div className="form-section">
                <div className="form-fields">
                  <div className="form-field half-width">
                    <label htmlFor="quotePrefix" className="modern-label">
                      Prefix
                    </label>
                    <input
                      type="text"
                      className="modern-input"
                      id="quotePrefix"
                      placeholder="Q"
                      value={quoteSettings.quoteNumbering.prefix}
                      onChange={(e) => updateQuoteSetting('quoteNumbering', { prefix: e.target.value })}
                    />
                  </div>
                  <div className="form-field half-width">
                    <label htmlFor="quoteStartNumber" className="modern-label">
                      Startnummer
                    </label>
                    <input
                      type="number"
                      className="modern-input"
                      id="quoteStartNumber"
                      placeholder="1"
                      value={quoteSettings.quoteNumbering.startNumber}
                      onChange={(e) => updateQuoteSetting('quoteNumbering', {
                        startNumber: parseInt(e.target.value, 10) || 1
                      })}
                    />
                  </div>
                  <div className="form-field half-width">
                    <label htmlFor="quotePadding" className="modern-label">
                      Antal siffror
                    </label>
                    <input
                      type="number"
                      className="modern-input"
                      id="quotePadding"
                      min="1"
                      max="10"
                      value={quoteSettings.quoteNumbering.padding}
                      onChange={(e) => updateQuoteSetting('quoteNumbering', {
                        padding: parseInt(e.target.value, 10) || 4
                      })}
                    />
                  </div>
                  <div className="form-field half-width">
                    <div className="form-check" style={{ marginTop: "2rem" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="quoteIncludeYear"
                        checked={quoteSettings.quoteNumbering.includeYear}
                        onChange={(e) => updateQuoteSetting('quoteNumbering', { includeYear: e.target.checked })}
                      />
                      <label className="form-check-label modern-label" htmlFor="quoteIncludeYear">
                        Inkludera år i offertnummer
                      </label>
                    </div>
                  </div>
                  <div className="form-field half-width">
                    <div className="form-check" style={{ marginTop: "2rem" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="quoteResetYearly"
                        checked={quoteSettings.quoteNumbering.resetYearly}
                        onChange={(e) => updateQuoteSetting('quoteNumbering', { resetYearly: e.target.checked })}
                      />
                      <label className="form-check-label modern-label" htmlFor="quoteResetYearly">
                        Återställ startnummer varje år
                      </label>
                    </div>
                  </div>
                </div>

                {/* Preview for Quote */}
                <div className="form-field full-width" style={{ marginTop: "1rem", padding: "1rem", background: "#f8f9fa", borderRadius: "8px" }}>
                  <label className="modern-label">Exempel på offertnummer:</label>
                  <div style={{ fontFamily: "monospace", fontSize: "1.1rem", color: "#495057" }}>
                    {quoteSettings.quoteNumbering.includeYear
                      ? `${quoteSettings.quoteNumbering.prefix}${new Date().getFullYear()}-${String(quoteSettings.quoteNumbering.startNumber).padStart(quoteSettings.quoteNumbering.padding, '0')}`
                      : `${quoteSettings.quoteNumbering.prefix}${String(quoteSettings.quoteNumbering.startNumber).padStart(quoteSettings.quoteNumbering.padding, '0')}`
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quote-specific settings can be added here */}
      <div className="modern-card" id="offertinstallningar">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12,6 12,12 16,14" />
              </svg>
              Offertinställningar
            </h2>
            <p className="section-subtitle">
              Konfigurera standardinställningar för dina offerter
            </p>
          </div>
          <div className="action-buttons">
            <Button
              variant="primary"
              className="ms-2"
              onClick={() => saveQuoteSettings()}
            >
              <Save className="me-2" size={16} />
              Spara offertinställningar
            </Button>
          </div>
        </div>

        <div className="modern-card-body">
          <div className="company-form-grid">
            <div className="form-section-wrapper">
              <div className="section-header-external">
                <div className="section-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12,6 12,12 16,14" />
                  </svg>
                </div>
                <div>
                  <h3 className="section-heading-external">Giltighetstid</h3>
                  <p className="section-description">Ange hur länge dina offerter ska vara giltiga</p>
                </div>
              </div>

              <div className="form-section">
                <div className="form-fields">
                  <div className="form-field half-width">
                    <label htmlFor="quoteValidityDays" className="modern-label">
                      Giltighetstid (dagar)
                    </label>
                    <input
                      type="number"
                      className="modern-input"
                      id="quoteValidityDays"
                      placeholder="30"
                      value={quoteSettings.defaultTerms?.validityDays || 30}
                      onChange={(e) => updateQuoteSetting('defaultTerms', {
                        validityDays: parseInt(e.target.value, 10) || 30
                      })}
                    />
                    <div className="field-hint">
                      Antal dagar som offerten är giltig från skapandedatum
                    </div>
                  </div>
                  <div className="form-field half-width">
                    <div className="form-check" style={{ marginTop: "2rem" }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="quoteAutoConvert"
                        checked={quoteSettings.defaultTerms?.autoConvertToInvoice || false}
                        onChange={(e) => updateQuoteSetting('defaultTerms', {
                          autoConvertToInvoice: e.target.checked
                        })}
                      />
                      <label className="form-check-label modern-label" htmlFor="quoteAutoConvert" style={{ marginBottom: 0 }}>
                        Automatisk konvertering till faktura när offert accepteras
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuotesSection;
